import { Injectable } from '@angular/core';
import { IBaseElement, ISeat, ISeatPayload } from '../interfaces';
import { FlattenSeat, GuidWrapper } from '../types';

export type SeatShapePreviewData = Pick<FlattenSeat, 'ruleTitle' | 'ruleTopText' | 'ruleBottomText' | 'w' | 'h'>;
import { Guid } from 'guid-typescript';
import { ShapeKeys, ShapeSeatTypes } from '../enums';
import { IDictionaryValue } from '@cinetixx/cinetixx-ui';
import { CreatorStateService } from '../services/app-services';
import { ShapeAttributesManager } from './shape-attributes.manager';
import { DEFAULT_SEAT_DATA, SEAT_TYPE_DISABLED_ID, SEAT_TYPE_SEAT_ID, SEAT_TYPE_WHEELCHAIR_ID } from '../constants';
import { HTMLElementUtils, ObjectUtils } from '@cinetixx/cinetixx-ui';
import { TextFormattingUtils } from '../utils';
import { ISeatSvgDimensions, MIGRATION_UNIFORM_SEAT_SIZE, SvgUtils } from '../utils/SvgUtils';
import { IShape } from '../interfaces/IShape';

@Injectable()
export class SeatManager {

  public static getHTMLElement(guid: string, container?: HTMLElement): HTMLElement {
    return (container ?? CreatorStateService.roomRef.nativeElement).querySelector<HTMLElement>(
      `[${ShapeAttributesManager.getAttribute(ShapeKeys.GUID)}="${guid}"]`
    ) || null;
  }

  public static getSeat(guid: string): GuidWrapper<ISeat> {
    return CreatorStateService.seatsPlan.seats.find((seat) => seat.guid === guid) ?? null;
  }

  public static getSeatByDefinitionId(id: number): GuidWrapper<ISeat> {
    return id ? (CreatorStateService.seatsPlan.seats.find((seat) => seat.id === id) ?? null) : null;
  }

  public static normalizeAllSeatsForNewCash(): void {
    if (!CreatorStateService.seatsPlan?.isForNewCashApp) {
      return;
    }

    for (const seat of CreatorStateService.seatsPlan.seats) {
      const shape = CreatorStateService.shapes.find((item) => item.id === seat.sectorElement.shapeId);

      if (shape) {
        SeatManager.applyDefaultSeatSizeToSectorElement(seat.sectorElement, shape);
      }
    }
  }

  public static shouldApplyDefaultSeatSize(
    w: number,
    h: number,
    expected: ISeatSvgDimensions
  ): boolean {
    if (!w || !h) {
      return true;
    }

    if (w === expected.w && h === expected.h) {
      return false;
    }

    if (w === MIGRATION_UNIFORM_SEAT_SIZE && h === MIGRATION_UNIFORM_SEAT_SIZE) {
      return expected.w !== MIGRATION_UNIFORM_SEAT_SIZE || expected.h !== MIGRATION_UNIFORM_SEAT_SIZE;
    }

    if (w === MIGRATION_UNIFORM_SEAT_SIZE && h === expected.h && w !== expected.w) {
      return true;
    }

    return false;
  }

  public static applyDefaultSeatSizeToSectorElement(
    sectorElement: ISeat['sectorElement'],
    shape: IShape
  ): boolean {
    if (!shape || shape.shapeSeatType === ShapeSeatTypes.NONE) {
      return false;
    }

    const expected = SvgUtils.getDefaultSeatSize(shape);

    if (!SeatManager.shouldApplyDefaultSeatSize(sectorElement.w, sectorElement.h, expected)) {
      return false;
    }

    const currentW = sectorElement.w || expected.w;
    const currentH = sectorElement.h || expected.h;
    const centerX = sectorElement.webX + currentW / 2;
    const centerY = sectorElement.webY + currentH / 2;

    sectorElement.w = expected.w;
    sectorElement.h = expected.h;
    sectorElement.webX = Math.round(centerX - expected.w / 2);
    sectorElement.webY = Math.round(centerY - expected.h / 2);

    return true;
  }

  public static getShapePreviewData(shape: IShape): SeatShapePreviewData {
    const size = SvgUtils.getDefaultSeatSize(shape);

    return {
      ruleTitle: shape.name,
      ruleTopText: 'I1',
      ruleBottomText: 'I2',
      w: size.w,
      h: size.h,
    };
  }

  public static buildLoveSeatDropPair(
    draggedShape: IShape,
    loveSeatsGroup: IShape[],
    dropSeat: GuidWrapper<ISeat>,
    loveSeatId: string
  ): Array<{ shape: IShape; data: Partial<GuidWrapper<ISeat>> }> {
    const leftShape = loveSeatsGroup.find((item) => item.shapeSeatType === ShapeSeatTypes.LOVE_SEAT_LEFT);
    const rightShape = loveSeatsGroup.find((item) => item.shapeSeatType === ShapeSeatTypes.LOVE_SEAT_RIGHT);

    if (!leftShape || !rightShape) {
      return [];
    }

    const leftSize = SvgUtils.getDefaultSeatSize(leftShape);
    const dropX = dropSeat.sectorElement.webX;
    const dropY = dropSeat.sectorElement.webY;

    let primaryShape: IShape;
    let secondaryShape: IShape;
    let primaryWebX: number;
    let secondaryWebX: number;

    if (draggedShape.shapeSeatType === ShapeSeatTypes.LOVE_SEAT_RIGHT) {
      primaryShape = rightShape;
      secondaryShape = leftShape;
      primaryWebX = dropX;
      secondaryWebX = dropX - leftSize.w;
    } else if (draggedShape.shapeSeatType === ShapeSeatTypes.SEAT && draggedShape.loveSeatId) {
      primaryShape = leftShape;
      secondaryShape = rightShape;
      primaryWebX = dropX - leftSize.w;
      secondaryWebX = dropX;
    } else {
      primaryShape = leftShape;
      secondaryShape = rightShape;
      primaryWebX = dropX;
      secondaryWebX = dropX + leftSize.w;
    }

    const buildData = (targetShape: IShape, webX: number, newGuid: boolean): Partial<GuidWrapper<ISeat>> => {
      const size = SvgUtils.getDefaultSeatSize(targetShape);

      return {
        ...dropSeat,
        ...(newGuid ? { guid: Guid.raw() } : {}),
        loveSeatId,
        sectorElement: {
          ...dropSeat.sectorElement,
          webX,
          webY: dropY,
          w: size.w,
          h: size.h,
          shapeId: targetShape.id,
        },
      };
    };

    return [
      { shape: primaryShape, data: buildData(primaryShape, primaryWebX, false) },
      { shape: secondaryShape, data: buildData(secondaryShape, secondaryWebX, true) },
    ];
  }

  public static transformHTMLElementToObject(el: HTMLElement): GuidWrapper<ISeat> {
    const seat = CreatorStateService.seatsPlan.seats.find((seat) => seat.guid === ShapeAttributesManager.getGUID(el));
    const shape = CreatorStateService.shapes.find(x => x.id === ShapeAttributesManager.getShapeId(el));

    const sectorElement = {
      webX: parseInt(el.style.left),
      webY: parseInt(el.style.top),
      w: el.style.width ? parseInt(getComputedStyle(el).width) : Math.max(25, el.offsetWidth),
      h: el.style.height ? parseInt(getComputedStyle(el).height) : el.offsetHeight,
    } as ISeat['sectorElement'];

    SeatManager.applyDefaultSeatSizeToSectorElement(sectorElement, shape);

    const baseData = {
      webX: sectorElement.webX,
      webY: sectorElement.webY,
      w: sectorElement.w,
      h: sectorElement.h,
      color: ShapeAttributesManager.getShapeBackgroundColor(el),
      shapeId: shape.id,
      rotate: HTMLElementUtils.getRotationDegrees(el.querySelector('.shape .editable-clip-path')),
    } as IBaseElement;

    if (seat) {
      return SeatManager.formatObject({
        ...seat,
        sectorElement: {
          ...seat.sectorElement,
          ...baseData
        },
        guid: ShapeAttributesManager.getGUID(el)
      });
    }

    return SeatManager.formatObject({
      sectorElement: baseData,
      seatTypeId: SeatManager.mapShapeSeatType(shape.shapeSeatType)?.id,
      guid: ShapeAttributesManager.getGUID(el)
    });
  }

  public static getDataToSend(seat: Partial<ISeat>): ISeatPayload {
    const shape = CreatorStateService.shapes.find(x => x.id === seat.sectorElement.shapeId);
    const flattenSeat = ObjectUtils.flat(seat) as FlattenSeat;
    const ruleTopText = seat.sectorElement.ruleTopText || DEFAULT_SEAT_DATA.sectorElement.ruleTopText;
    const ruleBottomText = seat.sectorElement.ruleBottomText || DEFAULT_SEAT_DATA.sectorElement.ruleBottomText;
    const ruleTipText = seat.sectorElement.ruleTipText || DEFAULT_SEAT_DATA.sectorElement.ruleTipText;
    const ruleTitle = seat.sectorElement.ruleTitle || DEFAULT_SEAT_DATA.sectorElement.ruleTitle;

    return {
      ...SeatManager.getBaseObject(seat),
      x: seat.sectorElement.x,
      webX: seat.sectorElement.webX,
      y: seat.sectorElement.y,
      webY: seat.sectorElement.webY,
      width: seat.sectorElement.w,
      height: seat.sectorElement.h,
      color: seat.sectorElement.color,
      flagSeatFac: seat.sectorElement.flagSeatFac ?? DEFAULT_SEAT_DATA.sectorElement.flagSeatFac,
      flagRankFac: seat.sectorElement.flagRankFac ?? DEFAULT_SEAT_DATA.sectorElement.flagRankFac,
      title: TextFormattingUtils.formatText(ruleTitle, flattenSeat) ?? DEFAULT_SEAT_DATA.sectorElement.title,
      titleSizeMin: DEFAULT_SEAT_DATA.sectorElement.titleSizeMin,
      titleSizeMax: DEFAULT_SEAT_DATA.sectorElement.titleSizeMax,
      image: shape.image,
      imageX: shape.imageX,
      imageY: shape.imageY,
      sectorId: seat.sectorElement.sectorId ?? null,
      shapeId: seat.sectorElement.shapeId || null,
      rotate: seat.sectorElement.rotate || 0,
      seatConfigurationId: CreatorStateService.seatsPlan.seatConfigurationId,
      seatDefinitionId: +seat.id || null,
      seatName: '',
      seatTypeId: seat.seatTypeId ?? SeatManager.mapShapeSeatType(shape.shapeSeatType)?.id,
      sectorElementId: seat?.sectorElement?.id || null,
      rankToken: seat.rankToken ?? '0',
      ruleTitle,
      ruleTopText,
      bottomText: TextFormattingUtils.formatText(ruleBottomText, flattenSeat) || DEFAULT_SEAT_DATA.sectorElement.bottomText,
      ruleBottomText,
      tipText: TextFormattingUtils.formatText(ruleTipText, flattenSeat) || DEFAULT_SEAT_DATA.sectorElement.tipText,
      ruleTipText,
      topText: TextFormattingUtils.formatText(ruleTopText, flattenSeat) || DEFAULT_SEAT_DATA.sectorElement.topText,
      loveSeatId: seat.loveSeatId ?? DEFAULT_SEAT_DATA.loveSeatId
    } as ISeatPayload;
  }

  public static formatObject(seat: Partial<GuidWrapper<ISeat>>): GuidWrapper<ISeat> {
    const shapeId = seat.sectorElement.shapeId ?? DEFAULT_SEAT_DATA.sectorElement.shapeId;
    const shape = CreatorStateService.shapes.find(x => x.id === shapeId);
    let seatTypeId = DEFAULT_SEAT_DATA.seatTypeId;

    if (!seat.seatTypeId && shape) {
      seatTypeId = SeatManager.mapShapeSeatType(shape.shapeSeatType)?.id;
    }

    const defaultSeatSize = shape
      ? SvgUtils.getDefaultSeatSize(shape)
      : { w: DEFAULT_SEAT_DATA.sectorElement.w, h: DEFAULT_SEAT_DATA.sectorElement.h };

    const sectorElement = {
      shapeId,
      x: seat.sectorElement.x ?? DEFAULT_SEAT_DATA.sectorElement.x,
      webX: seat.sectorElement.webX ?? DEFAULT_SEAT_DATA.sectorElement.webX,
      y: seat.sectorElement.y ?? DEFAULT_SEAT_DATA.sectorElement.y,
      webY: seat.sectorElement.webY ?? DEFAULT_SEAT_DATA.sectorElement.webY,
      w: seat.sectorElement.w || defaultSeatSize.w,
      h: seat.sectorElement.h || defaultSeatSize.h,
      title: seat.sectorElement.title ?? DEFAULT_SEAT_DATA.sectorElement.title,
      color: seat.sectorElement.color ?? DEFAULT_SEAT_DATA.sectorElement.color,
      flagRankFac: seat.sectorElement.flagRankFac ?? DEFAULT_SEAT_DATA.sectorElement.flagRankFac,
      flagSeatFac: seat.sectorElement.flagSeatFac ?? DEFAULT_SEAT_DATA.sectorElement.flagSeatFac,
      alignCurveId: seat.sectorElement.alignCurveId ?? DEFAULT_SEAT_DATA.sectorElement.alignCurveId,
      alignCurvePosition: seat.sectorElement.alignCurvePosition ?? DEFAULT_SEAT_DATA.sectorElement.alignCurvePosition,
      rotate: seat.sectorElement.rotate ?? DEFAULT_SEAT_DATA.sectorElement.rotate,
      seatConfigurationId: seat.sectorElement.seatConfigurationId ?? CreatorStateService.seatsPlan.seatConfigurationId,
      titleSizeMax: seat.sectorElement.titleSizeMax ?? DEFAULT_SEAT_DATA.sectorElement.titleSizeMax,
      titleSizeMin: seat.sectorElement.titleSizeMin ?? DEFAULT_SEAT_DATA.sectorElement.titleSizeMin,
      image: seat.sectorElement.image ?? DEFAULT_SEAT_DATA.sectorElement.image,
      imageX: seat.sectorElement.imageX ?? DEFAULT_SEAT_DATA.sectorElement.imageX,
      imageY: seat.sectorElement.imageY ?? DEFAULT_SEAT_DATA.sectorElement.imageY,
      id: seat.sectorElement.id ?? DEFAULT_SEAT_DATA.sectorElement.id,
      ruleTitle: seat.sectorElement.ruleTitle ?? DEFAULT_SEAT_DATA.sectorElement.ruleTitle,
      ruleTopText: seat.sectorElement.ruleTopText ?? DEFAULT_SEAT_DATA.sectorElement.ruleTopText,
      bottomText: seat.sectorElement.bottomText || DEFAULT_SEAT_DATA.sectorElement.bottomText,
      ruleBottomText: seat.sectorElement.ruleBottomText ?? DEFAULT_SEAT_DATA.sectorElement.ruleBottomText,
      tipText: seat.sectorElement.tipText ?? DEFAULT_SEAT_DATA.sectorElement.tipText,
      ruleTipText: seat.sectorElement.ruleTipText ?? DEFAULT_SEAT_DATA.sectorElement.ruleTipText,
      topText: seat.sectorElement.topText ?? DEFAULT_SEAT_DATA.sectorElement.topText,
      sectorId: seat.sectorElement.sectorId ?? DEFAULT_SEAT_DATA.sectorElement.sectorId,
    } as ISeat['sectorElement'];

    if (CreatorStateService.seatsPlan?.isForNewCashApp && shape) {
      SeatManager.applyDefaultSeatSizeToSectorElement(sectorElement, shape);
    }

    return {
      ...SeatManager.getBaseObject(seat),
      guid: seat.guid ?? Guid.raw(),
      sectorElement,
      rankToken: seat.rankToken ?? DEFAULT_SEAT_DATA.rankToken,
      seatTypeId: seat.seatTypeId ?? seatTypeId,
      termId: seat.termId ?? DEFAULT_SEAT_DATA.termId,
      seatCount: seat.seatCount ?? DEFAULT_SEAT_DATA.seatCount,
      areaId: seat.areaId ?? DEFAULT_SEAT_DATA.areaId,
      priceAreaColor: seat.priceAreaColor ?? DEFAULT_SEAT_DATA.priceAreaColor,
      ticketEntryText: seat.ticketEntryText ?? DEFAULT_SEAT_DATA.ticketEntryText,
      name: seat.name ?? DEFAULT_SEAT_DATA.name,
      id: seat.id ?? DEFAULT_SEAT_DATA.id
    } as GuidWrapper<ISeat>;
  }

  public static mapShapeSeatType(shapeSeatType: ShapeSeatTypes): IDictionaryValue {
    switch (shapeSeatType) {
      case ShapeSeatTypes.DISABLED:
        return CreatorStateService.seatTypes.find(x => x.id === SEAT_TYPE_DISABLED_ID);
      case ShapeSeatTypes.WHEELCHAIR:
        return CreatorStateService.seatTypes.find(x => x.id === SEAT_TYPE_WHEELCHAIR_ID);
      default:
        return CreatorStateService.seatTypes.find(x => x.id === SEAT_TYPE_SEAT_ID);
    }
  }

  private static getBaseObject(seat: Partial<GuidWrapper<ISeat>>): Partial<GuidWrapper<ISeat>> {
    return {
      seatNumber: seat?.seatNumber ?? DEFAULT_SEAT_DATA.seatNumber,
      rank: seat?.rank ?? DEFAULT_SEAT_DATA.rank,
      seatStatusId: seat?.seatStatusId ?? DEFAULT_SEAT_DATA.seatStatusId,
      leftSeatDefinitionId: seat?.leftSeatDefinitionId ?? DEFAULT_SEAT_DATA.leftSeatDefinitionId,
      rightSeatDefinitionId: seat?.rightSeatDefinitionId ?? DEFAULT_SEAT_DATA.rightSeatDefinitionId,
      loveSeatId: seat?.loveSeatId ?? DEFAULT_SEAT_DATA.loveSeatId,
      seatWeight: seat.seatWeight ?? DEFAULT_SEAT_DATA.seatWeight
    };
  }
}
